<template>
    <div class="w-full">
        <div v-if="enabled" class="transition-all duration-300">
            <div class="flex flex-col justify-center">
                <div class="flex items-start justify-between gap-2">
                    <div>
                        <span
                            v-if="label"
                            class="text-sm font-medium text-theme-base min-w-0"
                        >
                            {{ label }}
                        </span>

                        <custom-switch
                            v-model="activeComponent"
                            success
                            danger
                            class="w-24"
                            @input="onActiveInput"
                        >
                            <template #on>
                                <small>{{ onLabel }}</small>
                            </template>
                            <template #off>
                                <small>{{ offLabel }}</small>
                            </template>
                        </custom-switch>
                    </div>
                    <transition
                        enter-active-class="transition-all duration-300 ease-out"
                        enter-from-class="opacity-0 translate-x-2"
                        enter-to-class="opacity-100 translate-x-0"
                        leave-active-class="transition-all duration-200 ease-in"
                        leave-from-class="opacity-100 translate-x-0"
                        leave-to-class="opacity-0 translate-x-2"
                    >
                        <div
                            v-if="activeComponent && requireWeight"
                            class="flex items-center gap-4 flex-wrap"
                        >
                            <div
                                v-if="pageComponent"
                                class="flex items-center gap-1.5"
                            >
                                <label
                                    class="text-xs font-medium text-theme-muted uppercase tracking-wide"
                                >
                                    Página
                                </label>
                                <select
                                    :key="pageComponent"
                                    :value="pageComponent"
                                    class="appearance-none border rounded-lg px-3 py-1.5 pr-7 text-sm font-medium bg-theme-canvas text-theme-base shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-success focus:border-theme-success cursor-pointer transition-shadow duration-150"
                                    @change="onPageInput"
                                >
                                    <option
                                        v-for="p in resolvedPageOptions"
                                        :key="p.value"
                                        :value="p.value"
                                    >
                                        {{ p.label }}
                                    </option>
                                </select>
                            </div>

                            <div
                                class="hidden sm:block w-px h-6 bg-theme-border"
                            ></div>

                            <div class="flex items-center gap-1.5">
                                <label
                                    class="text-xs font-medium text-theme-muted uppercase tracking-wide"
                                >
                                    Peso
                                </label>
                                <input
                                    type="number"
                                    :value="weightComponent"
                                    min="1"
                                    class="w-20 border border-theme-border rounded-lg px-3 py-1.5 text-sm font-medium text-theme-base shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-success focus:border-theme-success transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    @change="onWeightInput"
                                />
                            </div>
                        </div>
                    </transition>
                </div>

                <a
                    v-if="activeComponent && resolvedPublicUrl"
                    :href="resolvedPublicUrl"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center gap-1.5 text-sm no-underline text-theme-muted hover:text-theme-base transition min-w-0"
                    :title="resolvedPublicUrl"
                >
                    <span class="relative flex h-2 w-2 flex-shrink-0">
                        <span
                            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75"
                        ></span>
                        <span
                            class="relative inline-flex rounded-full h-2 w-2 bg-theme-success"
                        ></span>
                    </span>
                    <span class="truncate">
                        {{ resolvedPublicLabel }}
                    </span>
                    <ExternalLinkIcon
                        class="w-3 h-3 flex-shrink-0 opacity-60"
                    />
                </a>
            </div>
        </div>

        <div v-else class="flex items-center gap-2">
            <span v-if="label" class="text-sm font-medium text-theme-base">
                {{ label }}
            </span>

            <div
                v-if="!active || !resolvedPublicUrl"
                class="inline-flex items-center gap-1.5 text-sm font-semibold"
            >
                <span
                    class="w-2.5 h-2.5 rounded-full"
                    :class="active ? 'bg-theme-success' : 'bg-theme-danger'"
                ></span>
                <span :class="active ? 'text-theme-success' : 'text-danger'">
                    {{ active ? onLabel : offLabel }}
                </span>
            </div>

            <a
                v-if="active && resolvedPublicUrl"
                :href="resolvedPublicUrl"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1.5 text-sm no-underline text-theme-muted hover:text-theme-base transition min-w-0"
                :title="resolvedPublicUrl"
            >
                <span class="relative flex h-2 w-2 flex-shrink-0">
                    <span
                        class="animate-ping absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75"
                    ></span>
                    <span
                        class="relative inline-flex rounded-full h-2 w-2 bg-theme-success"
                    ></span>
                </span>
                <span class="truncate">
                    {{ resolvedPublicLabel }}
                </span>
                <ExternalLinkIcon class="w-3 h-3 flex-shrink-0 opacity-60" />
            </a>
        </div>

        <app-dialog v-model="isConfirmOpen" @close="onActivateModalClosed">
            <template #header>
                <h2 class="text-theme-primary text-2xl">
                    {{ activateTitle }}
                </h2>
            </template>
            <div class="my-3 mx-2">
                <div class="w-10/12 mx-auto">
                    <span>{{ activateHint }}</span>
                </div>

                <div
                    class="flex items-center justify-center gap-4 flex-wrap mt-4"
                >
                    <div class="flex items-center gap-1.5">
                        <label
                            class="text-xs font-medium text-theme-muted uppercase tracking-wide"
                        >
                            Página
                        </label>
                        <select
                            :key="pageComponent"
                            :value="pageComponent"
                            class="appearance-none border rounded-lg px-3 py-1.5 pr-7 text-sm font-medium bg-theme-canvas text-theme-base shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-success focus:border-theme-success cursor-pointer transition-shadow duration-150"
                            @change="onPageInput"
                        >
                            <option
                                v-for="p in resolvedPageOptions"
                                :key="p.value"
                                :value="p.value"
                            >
                                {{ p.label }}
                            </option>
                        </select>
                    </div>

                    <div class="flex items-center gap-1.5">
                        <label
                            class="text-xs font-medium text-theme-muted uppercase tracking-wide"
                        >
                            Peso
                        </label>
                        <input
                            type="number"
                            :value="weightComponent"
                            min="1"
                            class="w-20 border border-theme-border rounded-lg px-3 py-1.5 text-sm font-medium text-theme-base shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-success focus:border-theme-success transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            @change="onWeightInput"
                        />
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="con-footer flex justify-center my-2">
                    <app-button @click="onConfirmActivate">
                        {{ confirmLabel }}
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { stripUrlProtocol } from "@/common/lib/url";

import ExternalLinkIcon from "@/components/icons/ExternalLinkIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "ActiveSelector",
    components: { AppDialog, ExternalLinkIcon },
    model: {
        prop: "active",
        event: "change"
    },
    props: {
        active: {
            type: Boolean,
            default: false,
            required: true
        },
        enabled: {
            type: Boolean,
            default: true
        },
        weight: {
            type: Number,
            default: 12
        },
        page: {
            type: Number,
            default: null
        },
        maxPage: {
            type: Number,
            default: null
        },
        pageOptions: {
            type: Array,
            default: null
        },
        pageSize: {
            type: Number,
            default: 12
        },
        requireWeight: {
            type: Boolean,
            default: false
        },
        publicUrl: {
            type: String,
            default: ""
        },
        publicLabel: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: ""
        },
        onLabel: {
            type: String,
            default: "Activado"
        },
        offLabel: {
            type: String,
            default: "Desactivado"
        },
        activateTitle: {
            type: String,
            default: "¿Quieres activarlo?"
        },
        activateHint: {
            type: String,
            default:
                "El peso determina el orden en el listado: entre menor el peso, antes aparece. Selecciona la página y el peso donde quieres que aparezca."
        },
        confirmLabel: {
            type: String,
            default: "Confirmar"
        }
    },
    emits: ["change", "weight-change", "page-change"],
    data() {
        return {
            activeComponent: false,
            weightComponent: this.weight,
            pageComponent: this.page,
            isConfirmOpen: false,
            activationConfirmed: false
        };
    },
    computed: {
        estimatedPage() {
            return Math.ceil(this.weightComponent / this.pageSize) || 1;
        },
        resolvedPageOptions() {
            const limit = this.maxPage || 12;
            const base = this.pageOptions
                ? this.pageOptions
                : Array.from({ length: limit }, (_, i) => ({
                      value: i + 1,
                      label: String(i + 1)
                  }));

            const current = this.pageComponent;
            if (current && !base.some((p) => p.value === current)) {
                return [...base, { value: current, label: String(current) }];
            }

            return base;
        },
        resolvedPublicUrl() {
            return this.publicUrl?.trim() || "";
        },
        resolvedPublicLabel() {
            if (this.publicLabel) return this.publicLabel;
            return stripUrlProtocol(this.resolvedPublicUrl);
        }
    },
    watch: {
        active(value) {
            this.activeComponent = value;
        },
        weight(value) {
            this.weightComponent = value;
            this.pageComponent = this.estimatedPage;
        },
        page(value) {
            this.pageComponent = value;
        }
    },
    created() {
        this.activeComponent = this.active;
        this.weightComponent = this.weight;
        this.pageComponent = this.page ?? this.estimatedPage;
    },
    methods: {
        onActiveInput(newValue) {
            if (newValue && this.requireWeight) {
                this.isConfirmOpen = true;
                return;
            }
            this.$emit("change", newValue);
        },
        onConfirmActivate() {
            this.activationConfirmed = true;
            this.isConfirmOpen = false;
        },
        onActivateModalClosed() {
            this.isConfirmOpen = false;
            if (this.activationConfirmed) {
                this.activationConfirmed = false;
                this.$emit("change", true);
                return;
            }
            this.activeComponent = false;
        },
        onWeightInput(event) {
            const newWeight = Math.max(1, Number(event.target.value));
            this.weightComponent = newWeight;
            this.pageComponent = Math.ceil(newWeight / this.pageSize) || 1;

            this.$emit("weight-change", newWeight);
        },
        onPageInput(event) {
            const newPage = Number(event.target.value);
            this.pageComponent = newPage;
            this.weightComponent = newPage * this.pageSize;

            this.$emit("page-change", this.pageComponent);
            this.$emit("weight-change", this.weightComponent);
        }
    }
};
</script>
